import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  InputAdornment,
  TableSortLabel,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { da } from 'date-fns/locale';
import SearchIcon from '@mui/icons-material/Search';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SyncIcon from '@mui/icons-material/Sync';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, subDays, addDays } from 'date-fns';
import { api } from '../../services/api';
import SplitRouteDialog from '../../components/common/SplitRouteDialog';
import SplitCargoDialog from '../../components/common/SplitCargoDialog';
import type { SplitableCargoInfo } from '../../components/common/SplitCargoDialog';
import type { PlaceResult } from '../../components/common/PlacesAutocomplete';
import type { RouteWithDetails, RouteCargoSummary, Trailer, SplitRouteResult } from '../../types';

// Cargo type translations
const CARGO_TYPE_LABELS: Record<string, string> = {
  PALLET_GOODS: 'Pallegods',
  GENERAL_CARGO: 'Stykgods',
  COOL: 'Køl',
  FREEZE: 'Frost',
  BULK_CARGO: 'Bulkgods',
  CONTAINER: 'Container',
  MACHINES: 'Maskiner',
  MISCELLANEOUS: 'Diverse',
  FTL: 'FTL',
  LTL: 'LTL',
  PTL: 'PTL',
};

function formatCargoType(type: string): string {
  return CARGO_TYPE_LABELS[type] || type;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
}

// Check if cargo is part of a split by parsing the note field
function isSplitCargo(note: string | null): boolean {
  if (!note) return false;
  return /^(?:Rute-split\s+)?\d+\/\d+\s*-\s*[^|]+\|\s*(?:Original rute|Original|Rute):/.test(note);
}

// Parse split position from note — returns position/total or null
function parseSplitPosition(note: string | null): { position: number; total: number } | null {
  if (!note) return null;
  const match = note.match(/^(?:Rute-split\s+)?(\d+)\/(\d+)\s*-/);
  if (!match) return null;
  return { position: parseInt(match[1]), total: parseInt(match[2]) };
}

// Check if this cargo is the archive copy (1/N) — should not be splittable
function isArchiveCopy(note: string | null): boolean {
  const pos = parseSplitPosition(note);
  return pos !== null && pos.position === 1;
}

type SortField = 'routeName' | 'trailer' | 'route' | 'date' | 'cargoCount';
type SortDirection = 'asc' | 'desc';

export default function RouteList() {
  const queryClient = useQueryClient();

  // Single day picker - defaults to today
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => new Date());
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [trailerFilter, setTrailerFilter] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Route split dialog state
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<RouteWithDetails | null>(null);

  // Single cargo split dialog state
  const [cargoSplitDialogOpen, setCargoSplitDialogOpen] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState<SplitableCargoInfo | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Format date for API (same from and to = single day)
  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;

  // Fetch routes
  const { data: routes, isLoading, error } = useQuery({
    queryKey: ['routes', dateStr],
    queryFn: () => api.getRoutes(dateStr, dateStr),
  });

  // Fetch trailers for filter dropdown
  const { data: trailers } = useQuery({
    queryKey: ['trailers'],
    queryFn: () => api.getTrailers(),
  });

  // Split route mutation
  const splitMutation = useMutation({
    mutationFn: async ({ routeId, params }: {
      routeId: string;
      params: {
        trailerId: string;
        cargoIds: string[];
        reloadAddress: PlaceResult;
        reloadDate: string;
        deliveryRouteOption: 'undispatched' | 'newRoute';
        deliveryRouteDriverId?: string;
        deliveryRouteTrailerId?: string;
      };
    }) => {
      return api.splitRoute(routeId, params);
    },
    onSuccess: (result: SplitRouteResult) => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      setSnackbar({
        open: true,
        message: `${result.splitResults.length} ordrer splittet succesfuldt`,
        severity: 'success',
      });
      setSplitDialogOpen(false);
      setSelectedRoute(null);
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Der opstod en fejl ved split',
        severity: 'error',
      });
    },
  });

  // Single cargo split mutation
  const cargoSplitMutation = useMutation({
    mutationFn: async ({ cargoId, reloadAddress, reloadDate }: {
      cargoId: string;
      reloadAddress: PlaceResult;
      reloadDate: string;
    }) => {
      return api.splitCargo(cargoId, {
        reloadAddress: {
          address: reloadAddress.address,
          city: reloadAddress.city,
          postcode: reloadAddress.postcode,
          country: reloadAddress.country,
          lat: reloadAddress.lat,
          lon: reloadAddress.lon,
          reloadDate,
        },
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      setSnackbar({
        open: true,
        message: data.newCargo1
          ? `Ordre splittet: 1/3: ${data.newCargo1.ref}, 2/3: ${data.originalRef}, 3/3: ${data.newCargo3.ref}`
          : `Re-split: ${data.originalRef} → nyt led: ${data.newCargo3.ref}`,
        severity: 'success',
      });
      setCargoSplitDialogOpen(false);
      setSelectedCargo(null);
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Der opstod en fejl ved split af ordre',
        severity: 'error',
      });
    },
  });

  // Unsplit cargo mutation
  const unsplitMutation = useMutation({
    mutationFn: (cargoId: string) => api.unsplitCargo(cargoId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      setSnackbar({
        open: true,
        message: `Deling fortrudt! Original ordre: ${data.originalRef}. Slettet: ${data.deletedCargo2Ref}, ${data.deletedCargo3Ref}`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Kunne ikke fortryde deling',
        severity: 'error',
      });
    },
  });

  // Sync documents mutation
  const syncDocumentsMutation = useMutation({
    mutationFn: (cargoId: string) => api.syncCargoDocuments(cargoId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['routes'] });
      setSnackbar({
        open: true,
        message: `Dokumenter synkroniseret! ${data.documentsSynced} dokumenter kopieret på tværs af ${data.cargosInSplit} ordrer`,
        severity: 'success',
      });
    },
    onError: (error: Error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Kunne ikke synkronisere dokumenter',
        severity: 'error',
      });
    },
  });

  // Unsplit all split cargos on a route (sequentially)
  const [isUnsplittingRoute, setIsUnsplittingRoute] = useState(false);
  const handleUnsplitRoute = async (route: RouteWithDetails) => {
    const splitCargos = route.cargos.filter(c => isSplitCargo(c.note));
    if (splitCargos.length === 0) return;

    setIsUnsplittingRoute(true);
    let succeeded = 0;
    let failed = 0;
    for (const cargo of splitCargos) {
      try {
        await api.unsplitCargo(cargo.id);
        succeeded++;
      } catch {
        failed++;
      }
    }
    setIsUnsplittingRoute(false);
    queryClient.invalidateQueries({ queryKey: ['routes'] });
    setSnackbar({
      open: true,
      message: failed === 0
        ? `Alle ${succeeded} delinger fortrudt`
        : `${succeeded} delinger fortrudt, ${failed} fejlede`,
      severity: failed === 0 ? 'success' : 'error',
    });
  };

  const handleOpenSplitDialog = (route: RouteWithDetails) => {
    setSelectedRoute(route);
    setSplitDialogOpen(true);
  };

  const handleOpenCargoSplitDialog = (cargo: RouteCargoSummary) => {
    setSelectedCargo(cargo);
    setCargoSplitDialogOpen(true);
  };

  const handleSplitCargo = async (cargoId: string, reloadAddress: PlaceResult, reloadDate: string) => {
    await cargoSplitMutation.mutateAsync({ cargoId, reloadAddress, reloadDate });
  };

  const handleSplitRoute = async (routeId: string, params: {
    trailerId: string;
    cargoIds: string[];
    reloadAddress: PlaceResult;
    reloadDate: string;
    deliveryRouteOption: 'undispatched' | 'newRoute';
    deliveryRouteDriverId?: string;
    deliveryRouteTrailerId?: string;
  }) => {
    return splitMutation.mutateAsync({ routeId, params });
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter and sort routes
  const filteredRoutes = useMemo(() => {
    if (!routes) return [];

    let filtered = routes.filter(route => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesRoute = route.routeName?.toLowerCase().includes(search);
        const matchesTrailer = route.trailerNumber.toLowerCase().includes(search);
        const matchesCargo = route.cargos.some(
          c => c.ref.toLowerCase().includes(search) ||
               c.customerName?.toLowerCase().includes(search)
        );
        if (!matchesRoute && !matchesTrailer && !matchesCargo) {
          return false;
        }
      }

      // Trailer filter
      if (trailerFilter && route.trailerId !== trailerFilter) {
        return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'routeName':
          comparison = (a.routeName || '').localeCompare(b.routeName || '', 'da');
          break;
        case 'trailer':
          comparison = a.trailerNumber.localeCompare(b.trailerNumber, 'da');
          break;
        case 'route':
          const aRoute = `${a.firstStop.city || ''} - ${a.lastStop.city || ''}`;
          const bRoute = `${b.firstStop.city || ''} - ${b.lastStop.city || ''}`;
          comparison = aRoute.localeCompare(bRoute, 'da');
          break;
        case 'date':
          comparison = new Date(a.destinationDate || a.createdAt).getTime() -
                       new Date(b.destinationDate || b.createdAt).getTime();
          break;
        case 'cargoCount':
          comparison = a.cargoCount - b.cargoCount;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [routes, searchTerm, trailerFilter, sortField, sortDirection]);

  // Sync from DORA handler
  const handleSyncFromDora = async () => {
    setIsSyncing(true);
    try {
      await api.syncFromDora();
      await queryClient.invalidateQueries({ queryKey: ['routes'] });
      setSnackbar({ open: true, message: 'Synkroniseret fra DORA', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Fejl ved synkronisering', severity: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  // Get unique trailers for filter
  const uniqueTrailers = useMemo(() => {
    if (!trailers) return [];
    return trailers;
  }, [trailers]);

  if (error) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Ruter</Typography>
        <Alert severity="error">
          Kunne ikke hente ruter. Kontroller din forbindelse.
        </Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Box>
        {/* Header with filters */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ mr: 1 }}>Ruter</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton
              size="small"
              onClick={() => setSelectedDate(prev => prev ? subDays(prev, 1) : subDays(new Date(), 1))}
            >
              <ChevronLeftIcon />
            </IconButton>
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              slotProps={{ textField: { size: 'small', sx: { width: 140 } } }}
            />
            <IconButton
              size="small"
              onClick={() => setSelectedDate(prev => prev ? addDays(prev, 1) : addDays(new Date(), 1))}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Tooltip title="Synk fra DORA">
            <IconButton
              size="small"
              onClick={handleSyncFromDora}
              disabled={isSyncing}
              color="primary"
            >
              {isSyncing ? <CircularProgress size={20} /> : <SyncIcon />}
            </IconButton>
          </Tooltip>
          <TextField
            size="small"
            placeholder="Søg..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 150 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={trailerFilter}
              onChange={(e) => setTrailerFilter(e.target.value)}
              displayEmpty
              sx={{ '& .MuiSelect-select': { py: 0.75 } }}
            >
              <MenuItem value="">Alle køretøjer</MenuItem>
              {uniqueTrailers.map((t: Trailer) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredRoutes.length} ruter
          </Typography>
        </Box>

        {/* Loading state */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty state */}
        {!isLoading && filteredRoutes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">
              Ingen ruter fundet i den valgte periode
            </Typography>
          </Box>
        )}

        {/* Routes */}
        {!isLoading && filteredRoutes.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Sort headers */}
            <Box sx={{ display: 'flex', gap: 2, px: 2, alignItems: 'center' }}>
              <TableSortLabel
                active={sortField === 'routeName'}
                direction={sortField === 'routeName' ? sortDirection : 'asc'}
                onClick={() => handleSort('routeName')}
              >
                <Typography variant="caption" fontWeight="bold">Rute</Typography>
              </TableSortLabel>
              <TableSortLabel
                active={sortField === 'trailer'}
                direction={sortField === 'trailer' ? sortDirection : 'asc'}
                onClick={() => handleSort('trailer')}
                sx={{ ml: 2 }}
              >
                <Typography variant="caption" fontWeight="bold">Køretøj</Typography>
              </TableSortLabel>
              <TableSortLabel
                active={sortField === 'route'}
                direction={sortField === 'route' ? sortDirection : 'asc'}
                onClick={() => handleSort('route')}
                sx={{ ml: 2 }}
              >
                <Typography variant="caption" fontWeight="bold">Rute</Typography>
              </TableSortLabel>
              <TableSortLabel
                active={sortField === 'date'}
                direction={sortField === 'date' ? sortDirection : 'asc'}
                onClick={() => handleSort('date')}
                sx={{ ml: 2 }}
              >
                <Typography variant="caption" fontWeight="bold">Dato</Typography>
              </TableSortLabel>
              <TableSortLabel
                active={sortField === 'cargoCount'}
                direction={sortField === 'cargoCount' ? sortDirection : 'asc'}
                onClick={() => handleSort('cargoCount')}
                sx={{ ml: 2 }}
              >
                <Typography variant="caption" fontWeight="bold">Ordrer</Typography>
              </TableSortLabel>
            </Box>

            {filteredRoutes.map((route) => (
              <RouteCard
                key={route.id}
                route={route}
                onSplit={handleOpenSplitDialog}
                onSplitCargo={handleOpenCargoSplitDialog}
                onUnsplitCargo={(cargoId) => unsplitMutation.mutate(cargoId)}
                onUnsplitRoute={handleUnsplitRoute}
                onSyncDocuments={(cargoId) => syncDocumentsMutation.mutate(cargoId)}
                isSplittingCargo={cargoSplitMutation.isPending}
                isUnsplitting={unsplitMutation.isPending}
                isUnsplittingRoute={isUnsplittingRoute}
                isSyncingDocs={syncDocumentsMutation.isPending}
              />
            ))}
          </Box>
        )}

        {/* Split Route Dialog (multi-cargo) */}
        <SplitRouteDialog
          open={splitDialogOpen}
          onClose={() => {
            setSplitDialogOpen(false);
            setSelectedRoute(null);
          }}
          route={selectedRoute}
          onSplit={handleSplitRoute}
          isLoading={splitMutation.isPending}
        />

        {/* Split Cargo Dialog (single cargo) */}
        <SplitCargoDialog
          open={cargoSplitDialogOpen}
          onClose={() => {
            setCargoSplitDialogOpen(false);
            setSelectedCargo(null);
          }}
          cargo={selectedCargo}
          onSplit={handleSplitCargo}
          isLoading={cargoSplitMutation.isPending}
        />

        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}

// Route card component
interface RouteCardProps {
  route: RouteWithDetails;
  onSplit: (route: RouteWithDetails) => void;
  onSplitCargo: (cargo: RouteCargoSummary) => void;
  onUnsplitCargo: (cargoId: string) => void;
  onUnsplitRoute: (route: RouteWithDetails) => void;
  onSyncDocuments: (cargoId: string) => void;
  isSplittingCargo?: boolean;
  isUnsplitting?: boolean;
  isUnsplittingRoute?: boolean;
  isSyncingDocs?: boolean;
}

function RouteCard({ route, onSplit, onSplitCargo, onUnsplitCargo, onUnsplitRoute, onSyncDocuments, isSplittingCargo, isUnsplitting, isUnsplittingRoute, isSyncingDocs }: RouteCardProps) {
  const routeDisplay = `${route.firstStop.city || 'Ukendt'} → ${route.lastStop.city || 'Ukendt'}`;

  return (
    <Card>
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        {/* Route header - single compact line */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: route.cargos.length > 0 ? 1 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
              {route.routeName || `Rute ${route.id.slice(0, 8)}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {routeDisplay}
            </Typography>
            <Chip
              icon={<LocalShippingIcon sx={{ fontSize: '0.9rem !important' }} />}
              label={route.trailerAlias || route.trailerNumber}
              size="small"
              variant="outlined"
              sx={{ height: 22 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {formatDate(route.destinationDate || route.createdAt)}
            </Typography>
            <Chip
              label={`${route.cargoCount} ordrer`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
            {route.cargos.some(c => isSplitCargo(c.note)) ? (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                startIcon={isUnsplittingRoute ? <CircularProgress size={16} /> : <CallMergeIcon />}
                disabled={isUnsplittingRoute}
                onClick={() => onUnsplitRoute(route)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Fortryd split
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CallSplitIcon />}
                disabled={route.cargoCount === 0}
                onClick={() => onSplit(route)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Split
              </Button>
            )}
          </Box>
        </Box>

        {/* Cargo table */}
        {route.cargos.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Ref</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Kunde</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Fra</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Til</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 0.5, width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {route.cargos.map((cargo) => (
                <TableRow key={cargo.id} hover>
                  <TableCell sx={{ py: 0.5 }}>{cargo.ref}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip
                      label={formatCargoType(cargo.type)}
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>{cargo.customerName || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{cargo.firstStop.city || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{cargo.lastStop.city || '-'}</TableCell>
                  <TableCell sx={{ py: 0.5, width: 80 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {isSplitCargo(cargo.note) ? (
                        <>
                          {/* Allow re-split on non-archive cargos (2/N, 3/N, ...) */}
                          {!isArchiveCopy(cargo.note) && (
                            <Tooltip title="Split igen">
                              <IconButton
                                size="small"
                                onClick={() => onSplitCargo(cargo)}
                                disabled={isSplittingCargo}
                              >
                                <CallSplitIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Fortryd deling">
                            <IconButton
                              size="small"
                              onClick={() => onUnsplitCargo(cargo.id)}
                              disabled={isUnsplitting}
                              color="warning"
                            >
                              <CallMergeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Synkroniser dokumenter">
                            <IconButton
                              size="small"
                              onClick={() => onSyncDocuments(cargo.id)}
                              disabled={isSyncingDocs}
                              color="info"
                            >
                              <SyncIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Split ordre">
                          <IconButton
                            size="small"
                            onClick={() => onSplitCargo(cargo)}
                            disabled={isSplittingCargo}
                          >
                            <CallSplitIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {route.cargos.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Ingen ordrer på denne rute
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
